import ErrorIcon from '@mui/icons-material/Error';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import { filtersSelectors } from 'app-components/flux/filters';
import { rootUiActions } from 'app-components/flux/root-ui';
import StdDialog from 'components/Dialogs/StdDialog';
import useQDialogs from 'components/QDialogs/useQDialogs';
import QIllustration from 'components/QIllustration';
import WatchlistSuccessModal from 'containers/SpendingWatchlist/components/WatchlistSuccessModal';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import TypeSelector from './TypeSelector';
import WatchlistForm from './WatchlistForm';

const useStyles = makeStyles()((theme) => ({
  root: {
    position: 'relative',
  },
  headlineIcon: {
    position: 'absolute',
  },
  tooltip: {
    backgroundColor: theme.palette.error.light,
    padding: 12,
    borderRadius: 8,
  },
  tooltipArrow: {
    color: theme.palette.error.light,
  },
  tooltipPopper: {
    marginBottom: '8px !important',
  },
  tooltipContent: {
    color: theme.palette.error.main,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  tooltipIcon: {
    marginLeft: 2,
    marginRight: 2,
    height: 18,
  },
}));

const typesData = Object.freeze({
  CHARTOFACCOUNT: ['Category', 'Categories', 'categories'],
  PAYEE: ['Payee', 'Payees', 'payeeNames'],
  TAG: ['Tag', 'Tags', 'tags'],
});

function WatchlistModal(props) {
  const { watchlist = null, replaceURL, onClose } = props;
  const { classes } = useStyles();

  const dispatch = useDispatch();

  const [initialType, setInitialType] = useState(null);
  const [formData, setFormData] = useState(null);
  const [overwriteInitialData, setOverwriteInitialData] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  // Functional updater so this callback never closes over stale formData and can have an empty
  // dep array. Field-level equality guards against Formik re-renders that produce a new object
  // reference even when the values haven't changed, which would otherwise trigger a cascade of
  // modal re-renders on every keystroke.
  const setFormDataWrapper = useCallback((updatedFormData) => {
    setFormData((prev) => {
      if (
        !prev ||
        prev.dirty !== updatedFormData.dirty ||
        prev.isSubmitting !== updatedFormData.isSubmitting ||
        prev.isValid !== updatedFormData.isValid ||
        prev.isValidating !== updatedFormData.isValidating ||
        prev.isDisabled !== updatedFormData.isDisabled ||
        prev.resetForm !== updatedFormData.resetForm ||
        prev.submitForm !== updatedFormData.submitForm ||
        prev.values !== updatedFormData.values ||
        prev.showFieldsNeedAttention !== updatedFormData.showFieldsNeedAttention
      ) {
        return updatedFormData;
      }
      return prev;
    });
  }, []);

  const filters = useSelector(filtersSelectors.getFilters);
  const { dialogAlert } = useQDialogs();
  const navigate = useNavigate();

  const isEditing = useMemo(() => watchlist !== null, [watchlist]);
  const filter = useMemo(
    () => (isEditing ? filters.find((x) => x.id === watchlist.filterId) : initialType),
    [isEditing, filters, watchlist, initialType],
  );
  const showTypeSelector = useMemo(() => !isEditing && filter === null, [isEditing, filter]);
  const showBackButton = useMemo(() => !isEditing && filter !== null, [isEditing, filter]);

  const displayDiscardAlert = useCallback(
    (callback) => {
      dialogAlert(
        'Discard changes',
        'Are you sure you want discard all the changes?',
        (btnObj) => {
          if (btnObj.btnPressed === 'Discard') {
            callback();
          }
        },
        ['Go Back', 'Discard'],
      );
    },
    [dialogAlert],
  );

  const onHandleClose = useCallback(
    (event, reason) => {
      const closeCb = () => {
        setInitialType(null);
        setFormData(null);
        setOverwriteInitialData(null);
        dispatch(rootUiActions.removeDialog('WATCHLIST_DIALOG'));

        if (replaceURL) {
          navigate('/watchlist', { replace: true });
        }
        onClose && onClose(successModal);
      };

      if (
        filter !== null &&
        formData?.dirty &&
        !['Enter', 'DATA_SAVED'].includes(reason) &&
        reason !== 'success-modal'
      ) {
        displayDiscardAlert(closeCb);
        return;
      }

      if (['Enter'].includes(reason)) {
        // isDisabled subsumes isValid (it includes time-range validation Formik doesn't know about)
        if (!formData?.isDisabled) {
          formData.submitForm();
        }
        return;
      }

      closeCb();
    },
    [filter, formData, successModal, dispatch, replaceURL, onClose, navigate, displayDiscardAlert],
  );

  const onTypeClick = useCallback((type) => setInitialType(type), [setInitialType]);

  const onHandleBack = useCallback(() => {
    if (filter !== null && formData?.dirty) {
      setOverwriteInitialData(formData.values);
    }

    onTypeClick(null);
  }, [onTypeClick, setOverwriteInitialData, filter, formData]);

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && showBackButton) {
        onHandleClose(event, 'Enter');
      } else if (event.key === 'Escape') {
        onHandleClose(event, 'Escape');
      }
    },
    [onHandleClose, showBackButton],
  );

  const memoForm = useMemo(
    () =>
      filter ? (
        <WatchlistForm
          typesData={typesData}
          watchlist={watchlist}
          isEditing={isEditing}
          filter={filter}
          setFormData={setFormDataWrapper}
          overwriteInitialData={overwriteInitialData}
          setSuccessModal={setSuccessModal}
        />
      ) : null,
    [watchlist, isEditing, filter, setFormDataWrapper, overwriteInitialData],
  );

  return (
    <>
      {successModal && (
        <WatchlistSuccessModal
          open
          onClose={onHandleClose}
          name={formData?.values?.name || ''}
          created={successModal === 'create'}
        />
      )}
      <StdDialog
        onBack={onHandleBack}
        onClose={onHandleClose}
        onKeyDown={onKeyDown}
        open={successModal === null}
        showBackButton={showBackButton}
        showCloseButton
        size='md'
        PaperProps={{ style: { maxHeight: 804 } }}
        title={`${isEditing ? 'Edit' : 'Create a'} Watchlist`}
      >
        <DialogContent className={classes.root}>
          <div className={classes.headlineIcon}>
            <QIllustration name='watchlistNew' size='lg' />
          </div>

          {/* Create Watchlist - Type Selector */}
          {showTypeSelector && <TypeSelector typesData={typesData} onTypeClick={onTypeClick} />}

          {/* Create/Edit Watchlist - Form */}
          {memoForm}
        </DialogContent>
        {/* Submit button lives here in DialogActions (not inside WatchlistForm) so the form layout
            stays independent of button placement and the modal can drive disabled state from isDisabled,
            which includes time-range validation that Formik's own isValid does not cover. */}
        {filter && (
          <>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', overflow: 'visible' }}>
              {/* Tooltip is shown (not just on hover) when a time range field needs attention after a submit attempt */}
              <Tooltip
                arrow
                classes={{
                  tooltip: classes.tooltip,
                  arrow: classes.tooltipArrow,
                  tooltipPlacementTop: classes.tooltipPopper,
                }}
                title={
                  <div className={classes.tooltipContent}>
                    <ErrorIcon className={classes.tooltipIcon} />
                    <Typography variant='bodyRegular'>Fields need attention</Typography>
                  </div>
                }
                placement='top-end'
                open={Boolean(formData?.showFieldsNeedAttention)}
              >
                <span>
                  <Button
                    id='watchlist-submit-button'
                    variant='contained'
                    disabled={formData?.isDisabled ?? true}
                    onClick={() => formData?.submitForm?.()}
                  >
                    {isEditing ? 'Save' : 'Create'}
                  </Button>
                </span>
              </Tooltip>
            </DialogActions>
          </>
        )}
      </StdDialog>
    </>
  );
}

WatchlistModal.propTypes = {
  watchlist: PropTypes.object,
  replaceURL: PropTypes.bool,
  onClose: PropTypes.func,
};

WatchlistModal.whyDidYouRender = true;

export default WatchlistModal;
